import { useTranslation } from 'react-i18next'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Can } from '../../contexts/PermissionsContext'
import { useLeaseCalendar } from '../../hooks/useQueries'
import { useFormatters } from '../../lib/format'

interface Props {
  org: string
  contractUid: string
  onOpenTab: (tab: string) => void
}

const warn = 'text-xs font-medium text-warning'

/**
 * The calendar of a commercial lease: term, next break and its notice deadline,
 * renewal window, tacit extension, renewal rent cap (spec 2026-10-25-14). It is
 * hidden outside the LEASE_COMMERCIAL subtree; without a rule pack for the
 * jurisdiction it says so and points at the clauses.
 */
export function CommercialLeaseCard({ org, contractUid, onOpenTab }: Props) {
  const { t } = useTranslation('contracts')
  const { formatDate, formatMoney } = useFormatters()
  const { data: cal } = useLeaseCalendar(org, contractUid)
  if (!cal || cal.status === 'not_applicable') return null

  const editClauses = (
    <Can perm="contracts.write">
      <Button size="sm" variant="outline" onClick={() => onOpenTab('clauses')} data-testid="commercial-lease-edit-clauses">
        {t('commercial_lease.edit_clauses')}
      </Button>
    </Can>
  )

  if (cal.status === 'not_configured') {
    return (
      <Card data-testid="commercial-lease-card" data-status="not_configured">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('commercial_lease.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground" data-testid="commercial-lease-not-configured">
            {t('commercial_lease.not_configured')}
          </p>
          {editClauses}
        </CardContent>
      </Card>
    )
  }

  const next = cal.next_break
  const years = cal.term ? Math.round((cal.term.months / 12) * 10) / 10 : null
  const window = cal.renewal_request_window
  const cap = cal.renewal_rent_capped
  const rentPerArea = cal.rent_per_area

  return (
    <Card data-testid="commercial-lease-card" data-status="configured">
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          {t('commercial_lease.title')}
          {cal.jurisdiction && <Badge variant="secondary">{cal.jurisdiction}</Badge>}
          {cal.to_regularize && (
            <Badge variant="warning" data-testid="commercial-lease-to-regularize">
              {t('commercial_lease.to_regularize')}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="space-y-1" data-testid="commercial-lease-term">
          <p className="text-xs uppercase text-muted-foreground">{t('commercial_lease.term')}</p>
          {cal.term ? (
            <>
              <p className="text-lg font-semibold">
                {t('commercial_lease.term_years', { years })}
              </p>
              <p className="text-xs text-muted-foreground">
                {cal.term.start ? formatDate(cal.term.start) : '—'} {'→'} {cal.term.end ? formatDate(cal.term.end) : '—'}
              </p>
              {cal.term.below_minimum && (
                <p className={warn} data-testid="commercial-lease-term-below-minimum">
                  {t('commercial_lease.term_below_minimum')}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )}
          <p className="text-xs text-muted-foreground" data-testid="commercial-lease-firm-term">
            {cal.firm_term
              ? t('commercial_lease.firm_term', {
                  value: cal.firm_term.value,
                  unit: t(`commercial_lease.unit_${(cal.firm_term.unit ?? '').toLowerCase()}`, { count: cal.firm_term.value, defaultValue: cal.firm_term.unit }),
                })
              : t('commercial_lease.firm_term_none')}
          </p>
          {cal.term_notice_deadline && (
            <p className="text-xs text-muted-foreground" data-testid="commercial-lease-term-notice-deadline">
              {t('commercial_lease.term_notice_deadline', { date: formatDate(cal.term_notice_deadline) })}
            </p>
          )}
          {cal.extension && (
            <p className="text-xs text-muted-foreground">
              {t('commercial_lease.extension', { date: formatDate(cal.extension.since ?? ''), months: cal.extension.total_months ?? 0 })}
            </p>
          )}
        </div>

        <div className="space-y-1" data-testid="commercial-lease-next-break">
          <p className="text-xs uppercase text-muted-foreground">{t('commercial_lease.next_break')}</p>
          {next ? (
            <>
              <p className="text-lg font-semibold">{formatDate(next.date)}</p>
              <p className="text-xs text-muted-foreground">
                {t(`commercial_lease.party_${next.party}`)} · {t(`commercial_lease.source_${next.source}`)}
              </p>
              <p className="text-xs text-muted-foreground" data-testid="commercial-lease-next-break-deadline">
                {t('commercial_lease.break_notice_deadline', { date: formatDate(next.notice_deadline) })}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t('commercial_lease.no_break')}</p>
          )}
          {cal.unreadable_break_options ? (
            <p className={warn}>{t('commercial_lease.unreadable_breaks', { count: cal.unreadable_break_options })}</p>
          ) : null}
        </div>

        <div className="space-y-1">
          <p className="text-xs uppercase text-muted-foreground">{t('commercial_lease.rent')}</p>
          {rentPerArea ? (
            <p className="text-lg font-semibold" data-testid="commercial-lease-rent-per-area">
              {t('commercial_lease.rent_per_area', { amount: formatMoney(rentPerArea.amount_minor, rentPerArea.currency) })}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">{t('commercial_lease.no_area')}</p>
          )}
          {cal.leased_area != null && (
            <p className="text-xs text-muted-foreground" data-testid="commercial-lease-leased-area">
              {cal.leased_area_basis
                ? t('commercial_lease.leased_area_basis', { area: cal.leased_area, basis: cal.leased_area_basis })
                : t('commercial_lease.leased_area', { area: cal.leased_area })}
            </p>
          )}
          <p className="text-xs text-muted-foreground" data-testid="commercial-lease-change-of-use">
            {cal.change_of_use_allowed == null
              ? t('commercial_lease.change_of_use_unknown')
              : cal.change_of_use_allowed
                ? t('commercial_lease.change_of_use_allowed')
                : t('commercial_lease.change_of_use_not_allowed')}
          </p>
          {window && (
            <p className="text-xs text-muted-foreground" data-testid="commercial-lease-renewal-window">
              {window.closes_on
                ? t('commercial_lease.renewal_window', { from: formatDate(window.opens_on ?? ''), to: formatDate(window.closes_on) })
                : t('commercial_lease.renewal_window_open', { from: formatDate(window.opens_on ?? '') })}
            </p>
          )}
          {cap && (
            <p className="text-xs text-muted-foreground" data-testid="commercial-lease-renewal-cap">
              {cap.capped ? t('commercial_lease.renewal_capped') : t(`commercial_lease.renewal_uncapped_${cap.reason ?? 'capped'}`, { defaultValue: t('commercial_lease.renewal_uncapped') })}
            </p>
          )}
          <p className="text-xs text-muted-foreground" data-testid="commercial-lease-index">
            {cal.index ? t('commercial_lease.index', { index: cal.index.toUpperCase() }) : t('commercial_lease.index_none')}
          </p>
          {cal.index_use_mismatch && <p className={warn} data-testid="commercial-lease-index-mismatch">{t('commercial_lease.index_mismatch')}</p>}
        </div>
      </CardContent>
      <CardContent className="pt-0">{editClauses}</CardContent>
    </Card>
  )
}
